import React, { useContext, useEffect } from "react";

import { Alert, App, Divider, Empty, Flex, Spin } from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";

import { TypeFichaDeSaude } from "@/Models/Domain/Saude/FIcha/FichaDeSaude";
import { EnumTipoUsuario } from "@/Models/Domain/AreaRestrita/Sessao";

import { SessaoARContext } from "@/AreaRestritta/Contexts/SessaoContext";
import { FichaDeSaudeDoAlunoService } from "@/Services/Domain/Saude/FichaDeSaudeDoAluno";
import { PropriedadesDeNotificacao } from "@/Utils/Notificacoes";
import { Formatacoes } from "@/Utils/Formatacao";
import { FichaDeSaude } from "./FichaDeSaude";

export const FichaDeSaudeDoAluno: React.FC<{
  codigoDoAluno: number;
  onTemFicha?: () => any;
  txtAtualizar?: React.ComponentProps<typeof FichaDeSaude>["txtAtualizar"],
  txtSalvar?: React.ComponentProps<typeof FichaDeSaude>["txtSalvar"],
  form?:React.ComponentProps<typeof FichaDeSaude>["form"],
  criarBotaoSubmit?:React.ComponentProps<typeof FichaDeSaude>["criarBotaoSubmit"],
  onSucess?:() => void;
}> = (props) => {

  const { notification } = App.useApp();
  const SessaoAR = useContext(SessaoARContext);

  const fichaPersistidaQuery = useQuery({
    queryKey: ["FichaDeSaudeDoAlunoService.listarFicha", props.codigoDoAluno],
    queryFn: async () => {
      if (props.codigoDoAluno == undefined) return undefined;
      return FichaDeSaudeDoAlunoService.listarFicha(props.codigoDoAluno);
    },
    enabled: false,
  });

  useEffect(() => {
    fichaPersistidaQuery.refetch();
  }, [props.codigoDoAluno]);

  const fichaEncontrada = fichaPersistidaQuery.data?.data.at(0);

  useEffect(() => {
    if(fichaEncontrada) props.onTemFicha?.()
  }, [fichaEncontrada])

  const inserirFicha = useMutation({
    mutationFn: async (dados: { codigoDoAluno: number; ficha: TypeFichaDeSaude }) => {
      return FichaDeSaudeDoAlunoService.inserir(dados.codigoDoAluno, dados.ficha);
    },
    onSuccess: () => {
        notification.open({...PropriedadesDeNotificacao.Sucesso("Ficha cadastrada com sucesso")});
        props?.onSucess?.();
        fichaPersistidaQuery.refetch();
    }
  });

  const atualizarFicha = useMutation({
    mutationFn: async (dados: { codigoDoAluno: number; ficha: TypeFichaDeSaude }) => {
      return FichaDeSaudeDoAlunoService.atualizar(dados.codigoDoAluno, dados.ficha);
    },
    onSuccess: () => {
        notification.open({...PropriedadesDeNotificacao.Sucesso("Ficha atualizada com sucesso")});
        props?.onSucess?.();
        fichaPersistidaQuery.refetch();
    },
  });

  const onFinish = (formulario: TypeFichaDeSaude ) => {
    if(props.codigoDoAluno == undefined) return;
    if(fichaEncontrada == undefined){
        inserirFicha.mutate({ codigoDoAluno: props.codigoDoAluno, ficha: formulario });
    } else {
        const payload: TypeFichaDeSaude = {
            codigo: fichaEncontrada.codigo,
             ...formulario
        };

        atualizarFicha.mutate({ codigoDoAluno: props.codigoDoAluno, ficha: payload });
    }
  }

  const isLoading = inserirFicha.isPending || atualizarFicha.isPending || fichaPersistidaQuery.isPending;

  if (SessaoAR?.sessao?.tipoDeUsuario !== EnumTipoUsuario.Responsavel)
    return <Empty description="A sessão atual não corresponde a uma sessão do tipo responsável" />;

  return (
      <React.Fragment>
          <Spin spinning={fichaPersistidaQuery.isPending}>
              {fichaPersistidaQuery.isFetched && fichaEncontrada == undefined && (
                  <React.Fragment>
                      <Alert
                          type="info"
                          showIcon
                          description={
                              <>
                                  Você ainda não cadastrou a ficha de saúde do aluno.
                                  <br />
                                  Preencha o formulário a seguir para resolver esta pendência.
                              </>
                          }
                      />

                      <Divider />
                  </React.Fragment>
              )}

              {fichaEncontrada?.ultimaAlteracaoUTC != undefined && (
                  <React.Fragment>
                      <Flex style={{ justifyContent: "flex-end" }}>
                          Última alteração: {Formatacoes.paraData(fichaEncontrada.ultimaAlteracaoUTC + "Z")?.toLocaleString()}
                      </Flex>

                      <Divider />
                  </React.Fragment>
              )}

              <FichaDeSaude
                  ficha={fichaEncontrada}
                  bloquearSubmissao={isLoading}
                  onFinish={onFinish}
                  txtAtualizar={props.txtAtualizar}
                  txtSalvar={props.txtSalvar}
                  form={props.form}
                  criarBotaoSubmit={props.criarBotaoSubmit}
              />
          </Spin>
      </React.Fragment>
  );
};
